import { useMemo } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Tab,
  TabList,
  Dropdown,
  Option,
  Spinner,
  Badge,
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuItemLink,
  MenuPopover,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
} from '@fluentui/react-components';
import DataTable, { type Column } from '../components/DataTable';
import { realLinkProps } from '../components/links/realLinkProps';
import { getPriorityOutreach, setPriorityRowStatus } from '../services/api';
import type { PriorityRow, PriorityBucket } from '../types';
import { usePriorityOutreachScopeParams } from './usePriorityOutreachScopeParams';

const BUCKETS: PriorityBucket[] = ['two_year_no_current', 'one_year_no_current', 'extension_current_pending'];
const RETURN_TYPES = ['1040', '1040 MFS', '1041', '1065', '1120', '1120S'];
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const currentYear = new Date().getFullYear() - 1;
const priorYear1 = currentYear - 1;

const BUCKET_LABELS: Record<PriorityBucket, string> = {
  two_year_no_current: '2-Year Repeat',
  one_year_no_current: `${priorYear1} Only`,
  extension_current_pending: `${currentYear} Extension Pending`,
};

function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.message.toLowerCase().includes('timed out');
}

function getErrorMessage(error: unknown): string {
  if (isTimeoutError(error)) {
    return 'The request timed out — the server may be busy. Retry.';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

export default function PriorityOutreachPage() {
  const { bucket, returnType, pageSize, skip, setBucket, setReturnType, setPageSize, setSkip } =
    usePriorityOutreachScopeParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const setStatusMutation = useMutation({
    mutationFn: ({ row, status, notes }: { row: PriorityRow; status: string; notes?: string }) =>
      setPriorityRowStatus(row.contactId, row.bucket, status, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['priority-outreach'] }),
  });

  const query = useQuery({
    queryKey: ['priority-outreach', bucket, returnType, pageSize, skip],
    queryFn: () => getPriorityOutreach({
      bucket,
      returnType: returnType || undefined,
      status: 'open',
      take: pageSize,
      skip,
    }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const changeBucket = (b: PriorityBucket) => {
    setBucket(b);
    setSkip(0);
  };

  const changeReturnType = (rt: string) => {
    setReturnType(rt);
    setSkip(0);
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setSkip(0);
  };

  const data = query.data;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const nextSkip = data?.nextSkip ?? null;
  const prevSkip = Math.max(0, skip - pageSize);
  const pageEnd = Math.min(skip + rows.length, total);

  const columns: Column<PriorityRow>[] = useMemo(() => [
    { key: 'contactName', header: 'Client', sortable: true, getValue: (r) => r.contactName },
    { key: 'returnType', header: 'Return', width: '80px' },
    {
      key: 'priorYearTotalBill', header: 'Last Bill', sortable: true, width: '110px',
      render: (r) => r.priorYearTotalBill != null ? `$${r.priorYearTotalBill.toFixed(0)}` : '—',
      getValue: (r) => r.priorYearTotalBill ?? 0,
    },
    {
      key: 'lastFiledAt', header: 'Last Filed', width: '120px',
      render: (r) => r.lastFiledAt ? new Date(r.lastFiledAt).toLocaleDateString() : '—',
    },
    {
      key: 'extensionFiledAt', header: 'Extension', width: '120px',
      render: (r) => r.extensionFiledAt ? new Date(r.extensionFiledAt).toLocaleDateString() : '',
    },
    { key: 'email', header: 'Email', render: (r) => r.email || '—' },
    { key: 'phone', header: 'Phone', render: (r) => r.phone || '—' },
    {
      key: 'actions', header: 'Actions', width: '160px',
      render: (r) => (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button appearance="subtle" size="small">Actions</Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItemLink {...realLinkProps(`/outreach?clientId=${encodeURIComponent(r.contactId)}`, { navigate })}>
                Send outreach
              </MenuItemLink>
              <MenuItem onClick={() => setStatusMutation.mutate({ row: r, status: 'contacted' })}>
                Mark contacted
              </MenuItem>
              <MenuItem onClick={() => setStatusMutation.mutate({ row: r, status: 'scheduled' })}>
                Mark scheduled
              </MenuItem>
              <MenuItem onClick={() => setStatusMutation.mutate({ row: r, status: 'opted_out' })}>
                Mark opted-out
              </MenuItem>
              <MenuItem onClick={() => setStatusMutation.mutate({ row: r, status: 'dismissed' })}>
                Dismiss
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      ),
    },
  ], [navigate, setStatusMutation]);

  return (
    <div style={{ padding: 24 }}>
      <h1>Priority Outreach</h1>
      <p>Prior-year clients who have not yet been scheduled for {currentYear}.</p>

      {query.isError && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>
            {getErrorMessage(query.error)}
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="transparent" onClick={() => query.refetch()}>
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}

      <TabList selectedValue={bucket} onTabSelect={(_, d) => changeBucket(d.value as PriorityBucket)}>
        {BUCKETS.map(b => (
          <Tab key={b} value={b}>
            {BUCKET_LABELS[b]}{' '}
            {data?.bucketCounts?.[b] != null && (
              <Badge appearance="filled" color="informative">{data.bucketCounts[b]}</Badge>
            )}
          </Tab>
        ))}
      </TabList>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '16px 0', flexWrap: 'wrap' }}>
        <span>Return type:</span>
        <Dropdown
          value={returnType || 'All'}
          selectedOptions={[returnType]}
          onOptionSelect={(_, d) => changeReturnType((d.optionValue as string) || '')}
        >
          <Option value="">All</Option>
          {RETURN_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
        </Dropdown>

        <Dropdown
          aria-label="Page size"
          value={`${pageSize} per page`}
          selectedOptions={[String(pageSize)]}
          onOptionSelect={(_, d) => {
            const next = Number(d.optionValue);
            if (Number.isFinite(next) && next > 0) changePageSize(next);
          }}
          style={{ minWidth: 140 }}
        >
          {PAGE_SIZE_OPTIONS.map(n => (
            <Option key={n} value={String(n)}>{`${n} per page`}</Option>
          ))}
        </Dropdown>

        {!data || rows.length === 0 ? (
          <Button appearance="primary" disabled>
            Create campaign for {BUCKET_LABELS[bucket]} ({total})
          </Button>
        ) : (
          <Button
            as="a"
            appearance="primary"
            {...realLinkProps(`/outreach?bucket=${encodeURIComponent(bucket)}${returnType ? `&returnType=${encodeURIComponent(returnType)}` : ''}`, { navigate })}
          >
            Create campaign for {BUCKET_LABELS[bucket]} ({total})
          </Button>
        )}

        {query.isFetching && !query.isLoading && <Spinner size="tiny" label="Refreshing..." />}
      </div>

      {query.isLoading && <Spinner label="Loading..." />}

      {!query.isLoading && !query.isError && total > 0 && (
        <p style={{ margin: '0 0 8px' }}>
          Showing {rows.length === 0 ? 0 : skip + 1}–{pageEnd} of {total} clients in {BUCKET_LABELS[bucket]}.
        </p>
      )}

      {!query.isLoading && !query.isError && rows.length === 0 && (
        <p style={{ color: '#666', marginTop: 16 }}>No clients in this bucket.</p>
      )}

      {!query.isLoading && !query.isError && rows.length > 0 && (
        <DataTable
          data={rows}
          columns={columns}
          rowKey="contactId"
          searchable
          searchKeys={['contactName', 'email', 'phone']}
          emptyMessage="No clients in this bucket."
          paginated
        />
      )}

      {!query.isLoading && !query.isError && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <Button
            disabled={skip === 0 || query.isFetching}
            onClick={() => setSkip(prevSkip)}
          >
            Previous
          </Button>
          <Button
            disabled={nextSkip == null || query.isFetching}
            onClick={() => nextSkip != null && setSkip(nextSkip)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
